/**
 * Dealer Volume Tree Page (Rose only)
 *
 * Interactive tree visualization of the dealer/customer hierarchy
 * with volume data from the Firestorm reporting API.
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { roseAPI } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import {
  Card, CardContent, CardHeader, CardTitle,
  Button
} from '../components/ui'
import {
  RefreshCw, Loader2, AlertCircle, ChevronRight, ChevronDown,
  Users, Package, TrendingUp, UserCheck, ShoppingCart, Search, X
} from 'lucide-react'

function formatNum(v) {
  if (v == null) return '-'
  return new Intl.NumberFormat('en-US').format(v)
}

function formatCurrency(v) {
  if (v == null) return '-'
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v)
}

function formatDate(dateStr) {
  if (!dateStr) return '-'
  const d = new Date(dateStr)
  if (isNaN(d.getTime()) || d.getFullYear() < 2000) return '-'
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export default function DealerTreePage() {
  const { currentClinicId } = useAuthStore()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchInput, setSearchInput] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedDealer, setSelectedDealer] = useState(null)
  const [dealerDetail, setDealerDetail] = useState(null)
  const [detailLoading, setDetailLoading] = useState(false)

  const isRose = currentClinicId === 'rose'

  const fetchData = useCallback(async () => {
    if (!isRose) return
    try {
      setLoading(true)
      setError(null)
      const res = await roseAPI.getDealerTree({ startDate: '2024-01-01', endDate: '2026-12-31' })
      if (res.data.success) setData(res.data.data)
    } catch (err) {
      console.error('[DEALER-TREE] Error:', err)
      setError(err.response?.data?.error?.message || err.message)
    } finally {
      setLoading(false)
    }
  }, [isRose])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const fetchDealerDetail = useCallback(async (dealer) => {
    setSelectedDealer(dealer)
    setDealerDetail(null)
    try {
      setDetailLoading(true)
      const res = await roseAPI.getDealer(dealer.id)
      if (res.data.success) setDealerDetail(res.data.data)
    } catch (err) {
      console.error('[DEALER-DETAIL] Error:', err)
    } finally {
      setDetailLoading(false)
    }
  }, [])

  // Compute max volume for proportional bars
  const maxVolume = useMemo(() => {
    if (!data?.flat_dealers) return 1
    return Math.max(1, ...data.flat_dealers.map(d => d.volume))
  }, [data])

  // Filter dealers by search
  const searchResults = useMemo(() => {
    if (!searchQuery || !data?.flat_dealers) return null
    const q = searchQuery.toLowerCase()
    return data.flat_dealers.filter(d =>
      (d.name && d.name.toLowerCase().includes(q)) ||
      (d.email && d.email.toLowerCase().includes(q)) ||
      String(d.id).includes(q)
    )
  }, [searchQuery, data])

  if (!isRose) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Dealer Volume Tree</h1>
          <p className="text-sm text-muted-foreground mt-1">Rose dealer hierarchy and volume analytics</p>
        </div>
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <AlertCircle className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground mb-1">Please switch to the Rose clinic to view this report.</p>
              <p className="text-xs text-muted-foreground">Use the clinic selector in the header to switch.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  const summary = data?.summary
  const orderSummary = data?.order_summary
  const tree = data?.tree || []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Dealer Volume Tree</h1>
          <p className="text-sm text-muted-foreground mt-1">Rose dealer hierarchy and volume analytics</p>
        </div>
        <Button onClick={fetchData} variant="outline" size="sm" disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {error && (
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-destructive">
              <AlertCircle className="w-5 h-5" />
              <span>{error}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {loading && !data && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          <span className="ml-3 text-muted-foreground">Loading dealer data from Firestorm...</span>
        </div>
      )}

      {data && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
            <Card>
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium mb-1">
                  <TrendingUp className="w-3.5 h-3.5" /> Total Volume
                </div>
                <div className="text-xl font-bold">{formatNum(summary?.total_volume)}</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium mb-1">
                  <Package className="w-3.5 h-3.5" /> Total PSV
                </div>
                <div className="text-xl font-bold">{formatNum(summary?.total_psv)}</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium mb-1">
                  <ShoppingCart className="w-3.5 h-3.5" /> Orders
                </div>
                <div className="text-xl font-bold">{formatNum(summary?.total_orders)}</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium mb-1">
                  <UserCheck className="w-3.5 h-3.5 text-green-500" /> Ambassadors
                </div>
                <div className="text-xl font-bold text-green-600">{formatNum(summary?.ambassadors)}</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium mb-1">
                  <Users className="w-3.5 h-3.5 text-blue-500" /> Customers
                </div>
                <div className="text-xl font-bold text-blue-600">{formatNum(summary?.customers)}</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium mb-1">
                  <ShoppingCart className="w-3.5 h-3.5" /> Revenue
                </div>
                <div className="text-xl font-bold">{formatCurrency(orderSummary?.total_revenue)}</div>
              </CardContent>
            </Card>
          </div>

          {/* Dealer Search */}
          <Card>
            <CardContent className="py-3 px-4">
              <form onSubmit={(e) => { e.preventDefault(); setSearchQuery(searchInput) }} className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    type="text"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder="Search by name, email, or dealer ID..."
                    className="w-full pl-9 pr-8 py-1.5 text-sm border rounded-md bg-background"
                  />
                  {searchInput && (
                    <button type="button" onClick={() => { setSearchInput(''); setSearchQuery('') }} className="absolute right-2 top-1/2 -translate-y-1/2">
                      <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                    </button>
                  )}
                </div>
                <Button type="submit" size="sm">Search</Button>
              </form>
            </CardContent>
          </Card>

          {/* Search Results */}
          {searchResults && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Search Results ({searchResults.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {searchResults.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">No dealers match your search</p>
                ) : (
                  <div className="border rounded-lg overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="text-left p-3 font-medium">Name</th>
                          <th className="text-left p-3 font-medium">Email</th>
                          <th className="text-left p-3 font-medium">Type</th>
                          <th className="text-right p-3 font-medium">Volume</th>
                          <th className="text-right p-3 font-medium">PSV</th>
                          <th className="text-right p-3 font-medium">Orders</th>
                          <th className="text-left p-3 font-medium">Enrolled</th>
                          <th className="p-3"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {searchResults.map(d => (
                          <tr key={d.id} className="border-t hover:bg-muted/30">
                            <td className="p-3 font-medium">{d.name}</td>
                            <td className="p-3 text-xs text-muted-foreground">{d.email || '-'}</td>
                            <td className="p-3">
                              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                                d.isCustomer
                                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                                  : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                              }`}>
                                {d.isCustomer ? 'CUSTOMER' : 'AMBASSADOR'}
                              </span>
                            </td>
                            <td className="p-3 text-right font-mono">{formatNum(d.volume)}</td>
                            <td className="p-3 text-right font-mono">{formatNum(d.psv)}</td>
                            <td className="p-3 text-right font-mono">{formatNum(d.orders)}</td>
                            <td className="p-3 text-xs">{formatDate(d.enrollDate)}</td>
                            <td className="p-3">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 text-xs"
                                onClick={() => fetchDealerDetail(d)}
                              >
                                Details
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Dealer Detail Panel */}
          {selectedDealer && (
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">
                    {selectedDealer.name}
                    <span className="text-xs text-muted-foreground font-normal ml-2">ID: {selectedDealer.id}</span>
                  </CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => { setSelectedDealer(null); setDealerDetail(null) }}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {detailLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                  </div>
                ) : dealerDetail ? (
                  <div className="space-y-4">
                    {/* Dealer info */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                      <div>
                        <div className="text-xs text-muted-foreground">Email</div>
                        <div className="font-medium">{dealerDetail.dealerInfo?.email || '-'}</div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Type</div>
                        <div className="font-medium">{dealerDetail.dealerInfo?.isCustomer ? 'Customer' : 'Ambassador'}</div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Status</div>
                        <div className={`font-medium ${dealerDetail.dealerInfo?.isActive ? 'text-green-600' : 'text-red-500'}`}>
                          {dealerDetail.dealerInfo?.isActive ? 'Active' : 'Inactive'}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Enrolled</div>
                        <div className="font-medium">{formatDate(dealerDetail.dealerInfo?.enrollDate)}</div>
                      </div>
                    </div>

                    {/* Activity stats */}
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
                      {[
                        ['Volume', dealerDetail.personalActivity?.volume],
                        ['PSV', dealerDetail.personalActivity?.psv],
                        ['Orders', dealerDetail.personalActivity?.orders],
                        ['Commissions', dealerDetail.personalActivity?.commissions],
                        ['Downline', dealerDetail.teamActivity?.totalDownlineSize],
                      ].map(([label, val]) => (
                        <div key={label} className="border rounded-lg p-3 text-center">
                          <div className="text-xs text-muted-foreground">{label}</div>
                          <div className="text-lg font-bold">{formatNum(val)}</div>
                        </div>
                      ))}
                    </div>

                    {/* Orders table */}
                    {dealerDetail.orders && dealerDetail.orders.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold mb-2">Orders ({dealerDetail.orders.length})</h4>
                        <div className="border rounded-lg overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead className="bg-muted/50">
                              <tr>
                                <th className="text-left p-2 font-medium">Date</th>
                                <th className="text-left p-2 font-medium">Product</th>
                                <th className="text-right p-2 font-medium">Qty</th>
                                <th className="text-right p-2 font-medium">Price</th>
                                <th className="text-right p-2 font-medium">Total</th>
                                <th className="text-right p-2 font-medium">PSV</th>
                                <th className="text-left p-2 font-medium">Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {dealerDetail.orders.map(o => (
                                <tr key={o.orderId} className="border-t">
                                  <td className="p-2">{formatDate(o.orderDate)}</td>
                                  <td className="p-2 font-mono">{o.productNumber}</td>
                                  <td className="p-2 text-right">{o.quantity}</td>
                                  <td className="p-2 text-right">{formatCurrency(o.priceEach)}</td>
                                  <td className="p-2 text-right font-medium">{formatCurrency(o.lineTotal)}</td>
                                  <td className="p-2 text-right">{formatNum(o.psvAmount)}</td>
                                  <td className="p-2">
                                    {o.isVoided ? (
                                      <span className="text-red-500">Voided</span>
                                    ) : o.isRecurring ? (
                                      <span className="text-blue-600">Recurring</span>
                                    ) : (
                                      <span className="text-green-600">Active</span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground py-4 text-center">Failed to load dealer details</p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Tree */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">
                Dealer Hierarchy ({summary?.total_dealers || 0} dealers)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {tree.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">No dealers found</p>
              ) : (
                <div className="space-y-1">
                  {tree.map(node => (
                    <TreeNode key={node.id} node={node} depth={0} maxVolume={maxVolume} onSelect={fetchDealerDetail} />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}

function TreeNode({ node, depth, maxVolume, onSelect }) {
  const [expanded, setExpanded] = useState(depth < 2)
  const hasChildren = node.children && node.children.length > 0
  const volumePct = maxVolume > 0 ? (node.volume / maxVolume) * 100 : 0

  return (
    <div>
      <div
        className={`flex items-center gap-2 py-2 px-3 rounded-md hover:bg-muted/50 transition-colors ${
          depth === 0 ? 'bg-muted/30' : ''
        }`}
        style={{ paddingLeft: `${depth * 24 + 12}px` }}
      >
        {/* Expand/collapse toggle */}
        <button
          onClick={() => setExpanded(e => !e)}
          className={`flex-shrink-0 w-5 h-5 flex items-center justify-center rounded ${
            hasChildren ? 'text-muted-foreground hover:text-foreground' : 'invisible'
          }`}
        >
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>

        {/* Type badge */}
        <span className={`flex-shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded ${
          node.isCustomer
            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
            : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
        }`}>
          {node.isCustomer ? 'CUSTOMER' : 'AMBASSADOR'}
        </span>

        {/* Active indicator */}
        <span className={`flex-shrink-0 w-2 h-2 rounded-full ${node.isActive ? 'bg-green-500' : 'bg-red-400'}`}
          title={node.isActive ? 'Active' : 'Inactive'}
        />

        {/* Name + email (clickable for details) */}
        <div className="min-w-0 flex-1">
          <button
            onClick={(e) => { e.stopPropagation(); onSelect(node) }}
            className="font-medium text-sm hover:underline text-left"
          >
            {node.name}
          </button>
          {node.email && (
            <span className="text-xs text-muted-foreground ml-2 hidden sm:inline">{node.email}</span>
          )}
        </div>

        {/* Metrics */}
        <div className="flex items-center gap-4 flex-shrink-0 text-xs">
          <div className="text-right w-16 hidden md:block">
            <div className="text-muted-foreground">Volume</div>
            <div className="font-semibold">{formatNum(node.volume)}</div>
          </div>
          <div className="text-right w-14 hidden md:block">
            <div className="text-muted-foreground">PSV</div>
            <div className="font-semibold">{formatNum(node.psv)}</div>
          </div>
          <div className="text-right w-14 hidden sm:block">
            <div className="text-muted-foreground">Orders</div>
            <div className="font-semibold">{formatNum(node.orders)}</div>
          </div>
          <div className="text-right w-20 hidden lg:block">
            <div className="text-muted-foreground">Enrolled</div>
            <div className="font-semibold">{formatDate(node.enrollDate)}</div>
          </div>

          {/* Volume bar */}
          <div className="w-20 hidden md:block">
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full ${node.isCustomer ? 'bg-blue-500' : 'bg-green-500'}`}
                style={{ width: `${Math.max(volumePct, volumePct > 0 ? 4 : 0)}%` }}
              />
            </div>
          </div>

          {/* Downline count */}
          {hasChildren && (
            <span className="text-muted-foreground w-8 text-right" title={`${node.children.length} direct`}>
              {node.children.length}
            </span>
          )}
        </div>
      </div>

      {/* Children */}
      {expanded && hasChildren && (
        <div>
          {node.children.map(child => (
            <TreeNode key={child.id} node={child} depth={depth + 1} maxVolume={maxVolume} onSelect={onSelect} />
          ))}
        </div>
      )}
    </div>
  )
}
